import { gql } from '../typed-gql';

/*
 * Static
 */

export const ROLE_FRAGMENT = gql`
    fragment roleFields on Role {
        roleId
        name
        isAdmin
    }
`;

export const LANGUAGE_FRAGMENT = gql`
    fragment languageFields on Language {
        languageId
        name
        nativeName
        locale
        isPlatformLocale
        isSpokenOption
        sortOrder
    }
`;

export const COUNTRY_FRAGMENT = gql`
    ${LANGUAGE_FRAGMENT}
    fragment countryFields on Country {
        countryId
        name
        abbreviation
        currency
        callingCode
        isInsuranceSupported
        areDxCodesSupported
        applyLateFees
        defaultAppointmentPrice
        lateFeePrice
        liveClientPhoneLine
        liveProviderPhoneLine
        autoClientPhoneLine
        autoProviderPhoneLine
        isSMSSupported
        isLoginSupported
        isRegistrationSupported
        isTherapySupported
        defaultLanguage {
            ...languageFields
        }
    }
`;

export const STATE_FRAGMENT = gql`
    fragment stateFields on State {
        name
        abbreviation
    }
`;

export const INSURANCE_CARRIER_FRAGMENT = gql`
    fragment insuranceCarrierFields on InsuranceCarrier {
        insuranceCarrierId
        name
        type
        clearingHouse
        credentialingParentId
        # Inlined rather than spreading insuranceCarrierFields — a fragment
        # cannot spread itself, and only the id and name are needed to label
        # the group. Resolved one level deep by the backend.
        credentialingParent {
            insuranceCarrierId
            name
        }
        isProviderLoadingRequired
        claimResubmissionType
        inNetwork
        defaultPayerId
        defaultPolicyExpiration
        paysAssessments
        includePriorAuth
        specialCptCode
        vobEnabled
        phoneCallSupportEnabled
        autoPanelNewProviders
        careCircleEnabled
        groupsEnabled
        reachlinkPlusEnabled
        coBrandingEnabled
        logoUrl
        logoIconUrl
        brandColors {
            primary
            secondary
        }
    }
`;

export const EAP_OPTIONS_FRAGMENT = gql`
    fragment eapOptionsFields on EAPOptions {
        isFormalReferral
        formalReferralContext
        isSafetySensitive
    }
`;

export const PROVIDER_CARRIER_FRAGMENT = gql`
    ${INSURANCE_CARRIER_FRAGMENT}
    fragment providerCarrierFields on ProviderCarrier {
        insuranceCarrierId
        carrier {
            ...insuranceCarrierFields
        }
        status
        dateApplied
        dateApproved
    }
`;

export const CPT_CODE_FRAGMENT = gql`
    fragment cptCodeFields on CptCode {
        code
    }
`;

export const TIMEZONE_FRAGMENT = gql`
    fragment timezoneFields on Timezone {
        name
    }
`;

export const PRONOUN_FRAGMENT = gql`
    fragment pronounFields on Pronoun {
        pronounId
        name
    }
`;

export const ETHNICITY_FRAGMENT = gql`
    fragment ethnicityFields on Ethnicity {
        ethnicityId
        name
    }
`;

export const SPECIALTY_FRAGMENT = gql`
    fragment specialtyFields on Specialty {
        specialtyId
        name
    }
`;

export const CREDENTIAL_FRAGMENT = gql`
    fragment credentialFields on Credential {
        credentialId
        name
        service
    }
`;

export const PARTNERSHIP_STATUS_FRAGMENT = gql`
    fragment partnershipStatusFields on PartnershipStatus {
        partnershipStatusId
        name
    }
`;

export const DRUG_FRAGMENT = gql`
    fragment drugFields on Drug {
        drugId
        name
        type
    }
`;

export const PSYCH_CONDITION_FRAGMENT = gql`
    fragment psychConditionFields on PsychCondition {
        psychConditionId
        name
    }
`;

export const EMOTION_FRAGMENT = gql`
    fragment emotionFields on Emotion {
        emotionId
        name
        type
    }
`;

export const DIAGNOSIS_CODE_FRAGMENT = gql`
    fragment diagnosisCodeFields on DiagnosisCode {
        code
        description
    }
`;

export const QUESTIONNAIRE_QUESTION_FRAGMENT = gql`
    fragment questionnaireQuestionFields on QuestionnaireQuestion {
        questionId
        type
        text
        label
        parent
        answers {
            answerId
            text
            interpretation
            label
        }
        yesInterpretation
        noInterpretation
    }
`;

export const QUESTIONNAIRE_FRAGMENT = gql`
    ${QUESTIONNAIRE_QUESTION_FRAGMENT}
    fragment questionnaireFields on Questionnaire {
        name
        description
        questions {
            ...questionnaireQuestionFields
        }
    }
`;

/*
 *  Pagination
 */
export const PAGE_INFO = gql`
    fragment pageInfoFields on PageInfo {
        hasNextPage
        hasPreviousPage
        startCursor {
            id
            timestamp
        }
        endCursor {
            id
            timestamp
        }
    }
`;

/*
 * Organizations
 */

export const ORGANIZATION_GROUP_FRAGMENT = gql`
    fragment organizationGroupFields on OrganizationGroup {
        organizationGroupId
        name
        type
        isActive
    }
`;

export const ORGANIZATION_FRAGMENT = gql`
    ${COUNTRY_FRAGMENT}
    ${ORGANIZATION_GROUP_FRAGMENT}
    fragment organizationFields on Organization {
        organizationId
        country {
            ...countryFields
        }
        name
        nickName
        abbreviation
        departments {
            ...organizationGroupFields
            numberOfMembers
        }
        teams {
            ...organizationGroupFields
            numberOfMembers
        }
        locations {
            ...organizationGroupFields
            numberOfMembers
        }
        status
        perMemberPlanAmount
        billableMemberCount
        photoUrl
        createdAt
        updatedAt
    }
`;

export const ORGANIZATION_PREVIEW_FRAGMENT = gql`
    ${COUNTRY_FRAGMENT}
    fragment organizationPreviewFields on OrganizationPreview {
        organizationId
        country {
            ...countryFields
        }
        name
        nickName
        abbreviation
        status
        photoUrl
        createdAt
        updatedAt
    }
`;

export const ORGANIZATION_PROFILE_FRAGMENT = gql`
    ${ORGANIZATION_PREVIEW_FRAGMENT}
    ${ORGANIZATION_GROUP_FRAGMENT}
    fragment organizationProfileFields on OrganizationProfile {
        organization {
            ...organizationPreviewFields
        }
        department {
            ...organizationGroupFields
        }
        team {
            ...organizationGroupFields
        }
        location {
            ...organizationGroupFields
        }
        status
    }
`;

/** @deprecated Unused — never spread in any operation (dead code; safe to remove). */
export const ORGANIZATION_PROFILE_PREVIEW_FRAGMENT = gql`
    ${ORGANIZATION_PREVIEW_FRAGMENT}
    ${ORGANIZATION_GROUP_FRAGMENT}
    fragment organizationProfilePreviewFields on OrganizationProfilePreview {
        organization {
            ...organizationPreviewFields
        }
        department {
            ...organizationGroupFields
        }
        team {
            ...organizationGroupFields
        }
        location {
            ...organizationGroupFields
        }
        status
    }
`;

/*
 * Users
 */

export const CONTACT_FRAGMENT = gql`
    fragment contactFields on Contact {
        type
        firstName
        lastName
        relationship
        phoneNumber
        email
        note
    }
`;

export const INVITE_GUEST_FRAGMENT = gql`
    fragment inviteGuestFields on InvitedGuest {
        firstName
        lastName
        email
        phoneNumber
        relationship
        consentForContact
        note
        isPending
        attachToAppointment
    }
`;

/** @deprecated Unused — never spread in any operation (dead code; safe to remove). */
export const ADDRESS_PREVIEW_FRAGMENT = gql`
    ${STATE_FRAGMENT}
    ${COUNTRY_FRAGMENT}

    fragment addressPreviewFields on AddressPreview {
        city
        state
        country {
            ...countryFields
        }
        zipCode
        isDefault
    }
`;

export const ADDRESS_FRAGMENT = gql`
    ${STATE_FRAGMENT}
    ${COUNTRY_FRAGMENT}

    fragment addressFields on Address {
        line1
        line2
        city
        state
        State {
            ...stateFields
        }
        country {
            ...countryFields
        }
        zipCode
        isDefault
        createdAt
    }
`;

export const CAREBOT_CONFIG_FRAGMENT = gql`
    fragment carebotConfigFields on CarebotConfiguration {
        name
        photoUrl
        clientAgreedToTerms
    }
`;

export const AVAILABILITY_OFFER_FRAGMENT = gql`
    fragment availabilityOfferFields on AvailabilityOffer {
        provider {
            userId
            name
            firstName
            lastName
            photoUrl
        }
        client {
            userId
            name
            firstName
            lastName
            photoUrl
        }
        status
        description
        adjustedPeriods {
            start
            end
        }
        updatedAt
    }
`;

export const PROVIDER_PROFILE_FRAGMENT = gql`
    ${CREDENTIAL_FRAGMENT}
    ${LANGUAGE_FRAGMENT}
    ${SPECIALTY_FRAGMENT}
    ${PROVIDER_CARRIER_FRAGMENT}
    ${STATE_FRAGMENT}

    fragment providerProfileFields on ProviderProfile {
        biography
        credential {
            ...credentialFields
        }
        credentialDate
        languages {
            ...languageFields
        }
        specialties {
            ...specialtyFields
        }
        carriers {
            ...providerCarrierFields
        }
        states
        States {
            ...stateFields
        }
        licenseNumber
        calendar {
            cronofyCalendarId
            cronofyAvailabilityRuleId
            cronofySub
            nylasGrantId
            nylasLocationData {
                state
                locationId
            }
        }
        sendSchedules
        scheduleTime
        acceptsNewClients
        acceptsEapClients
        acceptsZocdocClients
        acceptsComPsychClients
        zocdocTurboModeOn
        isAllowedToBook
        bookingPriority
        dailyAppointmentLimit
        canSameDayReschedule
        monthlyReschedulingLimit
        applyCancellationPenalty
        avStartInterval
        avFloor
        isStripeConnected
        stripeOnboardingCompletedAt
        guidanceConnectId
        npi
        isApproved
        status
        source
        recruitingStatus
        pausedFromStatus
        contractDocumentId
        contractStatus
        assignedRecruiter {
            userId
            name
            firstName
            lastName
            email
            photoUrl
        }
        payRates {
            type
            rate
        }
        messagingEnabled
    }
`;

export const CLINICAL_AI_RESTRICTION_FRAGMENT = gql`
    fragment clinicalAiRestrictionFields on ClinicalAiRestriction {
        reason
        country {
            name
            abbreviation
        }
        state {
            name
            abbreviation
        }
    }
`;

export const CLIENT_PROFILE_FRAGMENT = gql`
    ${INSURANCE_CARRIER_FRAGMENT}
    ${CREDENTIAL_FRAGMENT}
    ${STATE_FRAGMENT}
    ${ROLE_FRAGMENT}
    ${COUNTRY_FRAGMENT}
    ${PRONOUN_FRAGMENT}
    ${LANGUAGE_FRAGMENT}
    ${ETHNICITY_FRAGMENT}
    ${CAREBOT_CONFIG_FRAGMENT}
    ${AVAILABILITY_OFFER_FRAGMENT}
    ${EAP_OPTIONS_FRAGMENT}
    ${CLINICAL_AI_RESTRICTION_FRAGMENT}

    fragment clientProfileFields on ClientProfile {
        availabilityOffers {
            ...availabilityOfferFields
        }
        # Ewwww ... Must be a better way to do this
        assignedCareCoordinator {
            userId
            role {
                ...roleFields
            }
            country {
                ...countryFields
            }
            name
            firstName
            lastName
            photoUrl
            dateOfBirth
            gender
        }
        assignedProviders {
            userId
            role {
                ...roleFields
            }
            country {
                ...countryFields
            }
            name
            firstName
            lastName
            photoUrl
            dateOfBirth
            gender
            providerProfile {
                credential {
                    ...credentialFields
                }
                states
                isApproved
            }
        }
        sessionType
        sessionMode
        isPhoneCallSupported
        coverageType
        insuranceCarrier {
            ...insuranceCarrierFields
        }
        memberId
        copay
        selfPayPrice
        appointmentPrice
        hasInsurance
        isApprovedToBook
        isMinorConsentComplete
        state
        State {
            ...stateFields
        }
        sendAssessments
        prescribedAssessments
        assessmentsToTake
        sendFollowups
        followupCount
        lastFollowup
        toFollowup
        isIntakeComplete
        isHighRisk
        sessionTranscriptionsEnabled
        carebotConfig {
            ...carebotConfigFields
        }
        source
        status
        eapOptions {
            ...eapOptionsFields
        }
        clinicalAiRestriction {
            ...clinicalAiRestrictionFields
        }
    }
`;

export const PAYER_PROFILE_FRAGMENT = gql`
    ${INSURANCE_CARRIER_FRAGMENT}
    fragment payerProfileFields on PayerProfile {
        payer {
            ...insuranceCarrierFields
        }
    }
`;

export const BETA_ACCESS_SETTINGS_FRAGMENT = gql`
    fragment betaAccessSettingsFields on BetaAccessSettings {
        communityEnabled
    }
`;

// Common (non-profile) user fields shared by the full and role-scoped fragments.
export const USER_CORE_FRAGMENT = gql`
    ${ROLE_FRAGMENT}
    ${COUNTRY_FRAGMENT}
    ${PRONOUN_FRAGMENT}
    ${LANGUAGE_FRAGMENT}
    ${ETHNICITY_FRAGMENT}
    ${CONTACT_FRAGMENT}
    ${ADDRESS_FRAGMENT}
    ${BETA_ACCESS_SETTINGS_FRAGMENT}

    fragment userCoreFields on User {
        userId
        role {
            ...roleFields
        }
        country {
            ...countryFields
        }
        name
        firstName
        lastName
        nickName
        phoneNumber
        email
        isEmailVerified
        calendarEmail
        emergencyContact {
            ...contactFields
        }
        photoUrl
        dateOfBirth
        gender
        pronoun {
            ...pronounFields
        }
        language {
            ...languageFields
        }
        ethnicity {
            ...ethnicityFields
        }
        timezone
        addresses {
            ...addressFields
        }
        notes
        betaAccessSettings {
            ...betaAccessSettingsFields
        }
        sendConfirmations
        sendCancellations
        sendReminders
        sendIcsAttachments
        closeId
        isRegistrationComplete
        suspensionReason
        suspensionDetails {
            reason
            suspendedAt
            acknowledgedAt
            appointmentId
        }
        isActive
        trustedUserIds
        createdAt
        deletedAt
        roles
        isMultiRole
    }
`;

// Full user with all four profiles. Use ONLY where the role is unknown at
// request time or a single object is reused across roles: GET_USER (generic +
// multi-role tab switch) and VERIFY_LOGIN_CODE (feeds JWTContext/useAuth).
// Role-specific queries/mutations should use the scoped variants below to avoid
// fetching irrelevant (null) profiles.
export const USER_FRAGMENT = gql`
    ${USER_CORE_FRAGMENT}
    ${CLIENT_PROFILE_FRAGMENT}
    ${PROVIDER_PROFILE_FRAGMENT}
    ${ORGANIZATION_PROFILE_FRAGMENT}
    ${PAYER_PROFILE_FRAGMENT}

    fragment userFields on User {
        ...userCoreFields
        careCircleEnabled
        groupsEnabled
        reachlinkPlusEnabled
        coBranding {
            insuranceCarrierId
            carrierName
            logoUrl
            logoIconUrl
            brandColors {
                primary
                secondary
            }
        }
        clientProfile {
            ...clientProfileFields
        }
        providerProfile {
            ...providerProfileFields
        }
        organizationProfile {
            ...organizationProfileFields
        }
        payerProfile {
            ...payerProfileFields
        }
    }
`;

// Role-scoped user fragments: core + only the relevant profile(s).
export const CLIENT_USER_FRAGMENT = gql`
    ${USER_CORE_FRAGMENT}
    ${CLIENT_PROFILE_FRAGMENT}
    fragment clientUserFields on User {
        ...userCoreFields
        careCircleEnabled
        groupsEnabled
        reachlinkPlusEnabled
        coBranding {
            insuranceCarrierId
            carrierName
            logoUrl
            logoIconUrl
            brandColors {
                primary
                secondary
            }
        }
        clientProfile {
            ...clientProfileFields
        }
    }
`;

export const PROVIDER_USER_FRAGMENT = gql`
    ${USER_CORE_FRAGMENT}
    ${PROVIDER_PROFILE_FRAGMENT}
    fragment providerUserFields on User {
        ...userCoreFields
        providerProfile {
            ...providerProfileFields
        }
    }
`;

export const ORG_MEMBER_USER_FRAGMENT = gql`
    ${USER_CORE_FRAGMENT}
    ${CLIENT_PROFILE_FRAGMENT}
    ${ORGANIZATION_PROFILE_FRAGMENT}
    fragment orgMemberUserFields on User {
        ...userCoreFields
        careCircleEnabled
        groupsEnabled
        reachlinkPlusEnabled
        coBranding {
            insuranceCarrierId
            carrierName
            logoUrl
            logoIconUrl
            brandColors {
                primary
                secondary
            }
        }
        clientProfile {
            ...clientProfileFields
        }
        organizationProfile {
            ...organizationProfileFields
        }
    }
`;

/** @deprecated Unused — never spread in any operation (dead code; safe to remove). */
export const CLIENT_PROFILE_PREVIEW_FRAGMENT = gql`
    ${INSURANCE_CARRIER_FRAGMENT}
    ${STATE_FRAGMENT}
    ${EAP_OPTIONS_FRAGMENT}
    fragment clientProfilePreviewFields on ClientProfilePreview {
        coverageType
        insuranceCarrier {
            ...insuranceCarrierFields
        }
        state
        State {
            ...stateFields
        }
        sessionType
        sessionMode
        sendFollowups
        followupCount
        lastFollowup
        toFollowup
        isIntakeComplete
        isHighRisk
        status
        eapOptions {
            ...eapOptionsFields
        }
    }
`;
/** @deprecated Unused — never spread in any operation (dead code; safe to remove). */
export const PROVIDER_PROFILE_PREVIEW_FRAGMENT = gql`
    ${CREDENTIAL_FRAGMENT}
    ${PROVIDER_CARRIER_FRAGMENT}
    fragment providerProfilePreviewFields on ProviderProfilePreview {
        credential {
            ...credentialFields
        }
        carriers {
            ...providerCarrierFields
        }
        states
        acceptsNewClients
        acceptsEapClients
        bookingPriority
        states
        acceptsNewClients
        isApproved
        status
        source
        recruitingStatus
        languageIds
        specialtyIds
    }
`;

/**
 * Minimal user selection for embedded/list contexts (avatar + name + link).
 * Baseline user selection for embedded/list/picker contexts (avatar + name +
 * link). Add role-specific extras inline at the embed/query site (see
 * appointmentPreviewFields and the user search/list queries in queries.ts).
 */
export const USER_MINI_FRAGMENT = gql`
    fragment userMiniFields on UserPreview {
        userId
        name
        firstName
        lastName
        nickName
        photoUrl
        deletedAt
    }
`;

/*
 * Benefit Verification (VOB)
 */

export const BENEFIT_VERIFICATION_FRAGMENT = gql`
    fragment benefitVerificationFields on BenefitVerification {
        benefitVerificationId
        policyId
        type
        status
        source
        failureReason
        isEligible
        outcome
        estimatedCopay
        planEndDate
        details
        createdAt
        updatedAt
    }
`;

export const POLICY_FRAGMENT = gql`
    ${INSURANCE_CARRIER_FRAGMENT}
    ${USER_MINI_FRAGMENT}
    ${BENEFIT_VERIFICATION_FRAGMENT}
    fragment policyFields on Policy {
        policyId
        userId
        insuranceCarrier {
            ...insuranceCarrierFields
        }
        type
        payerId
        memberId
        copay
        deductible
        authorizationNumber
        authorizedSessions
        authorizationExpiration
        memberFirstName
        memberLastName
        memberDob
        memberSex
        memberRelationship
        frontPhotoUrl
        backPhotoUrl
        vobEnabled
        isPrimary
        createdAt
        updatedAt

        authorizedProvider {
            ...userMiniFields
        }
        usedSessions
        latestVob {
            ...benefitVerificationFields
        }
        frontPhotoUrl
        backPhotoUrl
    }
`;

export const POLICY_PREVIEW_FRAGMENT = gql`
    ${INSURANCE_CARRIER_FRAGMENT}
    fragment policyPreviewFields on Policy {
        policyId
        userId
        insuranceCarrier {
            ...insuranceCarrierFields
        }
        type
        payerId
        memberId
        copay
        deductible
        authorizationNumber
        authorizedSessions
        authorizationExpiration
        memberFirstName
        memberLastName
        memberDob
        memberSex
        memberRelationship
        isPrimary
        createdAt
        updatedAt
    }
`;

/*
 * Intake
 */

export const MEDICAL_INTAKE_FRAGMENT = gql`
    ${ETHNICITY_FRAGMENT}
    fragment medicalIntakeFields on MedicalIntake {
        ethnicity {
            ...ethnicityFields
        }
        reasonForTherapy
        problemSeverity
        goalsForTherapy
        hasChronicPain
        drinksAlcohol
        drinksPerDay
        usesNicotine
        usesRecDrugs
        prescriptionDrugs {
            drugId
            name
        }
        recreationalDrugs {
            drugId
            name
        }
        recDrugFrequency
        psychConditions {
            psychConditionId
            name
        }
        hasHealthyDiet
        eatsThreeToFiveMeals
        exerciseFrequency
        partnershipStatus {
            partnershipStatusId
            name
        }
        sleepQuality
        wishedDead
        feelsBetterOffDead
        hadRecentSuicidalThoughts
        madeSuicidalAttempt
        suicidalAttemptHow
        suicidalAttemptWhen
        suicideTreatment
        hasCurrentSuicidalThoughts
        currentSuicidalThoughtsDescription
        gad7
        phq9
    }
`;

/*
 * Uploads
 */
export const UPLOAD_FRAGMENT = gql`
    fragment uploadFields on Upload {
        uploadId
        status
        resultData
        error
        completedAt
        createdAt
    }
`;

export const UPLOAD_URL_RESPONSE_FRAGMENT = gql`
    fragment uploadUrlResponseFields on UploadUrlResponse {
        uploadId
        url
        fields
        expiresAt
    }
`;

/*
 * Documents
 */
export const DOCUMENT_FRAGMENT = gql`
    ${USER_MINI_FRAGMENT}
    fragment documentFields on Document {
        documentId
        uploader {
            ...userMiniFields
        }
        title
        description
        fileName
        fileUrl
        fileSize
        fileType
        credentialDocumentType
        createdAt
    }
`;

/*
 * Provider credentialing
 */

/** A provider's therapy license in a single state, with the scanned certificate. */
export const LICENSE_FRAGMENT = gql`
    ${DOCUMENT_FRAGMENT}
    fragment licenseFields on License {
        licenseId
        state
        licenseNumber
        licenseType
        issueDate
        expirationDate
        expirationStatus
        daysUntilExpiration
        licenseDocumentId
        licenseDocument {
            ...documentFields
        }
        createdAt
        updatedAt
    }
`;

/**
 * The credentialing fields the provider profile reads and writes.
 * `consentHistory` is left out: it belongs to the rescinsion screen, not this one.
 */
export const PROVIDER_CREDENTIALING_DATA_FRAGMENT = gql`
    ${DOCUMENT_FRAGMENT}
    ${LICENSE_FRAGMENT}
    fragment providerCredentialingDataFields on ProviderCredentialingData {
        ssn
        caqhUsername
        caqhPassword
        caqhIdNumber
        npi
        primaryTaxonomy
        degree
        universityName
        universityAddress
        eduStartDate
        eduEndDate
        consentSigned
        consentFullName
        consentDOB
        consentSignedDate
        signedIPv4
        diplomaDocumentId
        diplomaDocument {
            ...documentFields
        }
        resumeDocumentId
        resumeDocument {
            ...documentFields
        }
        headshotDocumentId
        headshotDocument {
            ...documentFields
        }
        liabilityInsuranceDocumentId
        liabilityInsuranceDocument {
            ...documentFields
        }
        photoIdDocumentId
        photoIdDocument {
            ...documentFields
        }
        consentFormDocumentId
        consentFormDocument {
            ...documentFields
        }
        licenses {
            ...licenseFields
        }
        onboardingProgress {
            percentComplete
            completedSections
            totalSections
            isComplete
            hasExpiredLicense
            sections {
                section
                status
                completedFields
                totalFields
                missingFields
            }
        }
    }
`;

/*
 * Releases
 */
export const RELEASE_FRAGMENT = gql`
    ${DOCUMENT_FRAGMENT}
    fragment releaseFields on Release {
        releaseId
        description
        purpose
        authorizedPartyName
        authorizedPartyContact
        attachments {
            ...documentFields
        }
        dateSigned
        rescinsionDate
        expirationDate
        createdAt
        updatedAt
    }
`;

/*
 * Client Plans
 */

export const CLIENT_PLAN_FRAGMENT = gql`
    fragment clientPlanFields on ClientPlan {
        clientPlanId
        type
        isSigned
        dateSigned
        createdAt
        body {
            ... on TreatmentPlanBody {
                presentingProblems
                presentingProblemDetails
                longTermGoals
                longTermGoalDetails
                otherInformation
                otherInformationDetails
                strengthsAndResources
                strengthsAndResourcesDetails
            }
            ... on SafetyPlanBody {
                triggers
                triggerDetails
                goodDistractions
                goodDistractionDetails
                safePeople
                safePeopleDetails
                helpfulThings
                helpfulThingDetails
                safetyMethods
                safetyMethodDetails
                otherResourses
                otherResourseDetails
            }
        }
    }
`;

/*
 * Journals
 */
export const INFERRED_JOURNAL_EMOTION_FRAGMENT = gql`
    fragment inferredJournalEmotionFields on InferredJournalEmotion {
        emotionId
        name
        emoji
    }
`;

export const INFERRED_JOURNAL_THEME_FRAGMENT = gql`
    fragment inferredJournalThemeFields on InferredJournalTheme {
        themeId
        name
        emoji
    }
`;

export const JOURNAL_FRAGMENT = gql`
    ${INFERRED_JOURNAL_EMOTION_FRAGMENT}
    ${INFERRED_JOURNAL_THEME_FRAGMENT}
    fragment journalFields on Journal {
        journalId
        body
        mood
        visibility
        status
        createdAt
        categories
        subcategories
        journalInference {
            journalInferenceId
            journalId
            summary
            title
            emotions {
                ...inferredJournalEmotionFields
            }
            themes {
                ...inferredJournalThemeFields
            }
            status
            createdAt
            updatedAt
        }
    }
`;

export const JOURNAL_SUBMISSION_FRAGMENT = gql`
    fragment journalSubmissionFields on Journal {
        body
        mood
        categories
        subcategories
        createdAt
    }
`;

/** @deprecated Unused — never spread in any operation (dead code; safe to remove). */
export const PAGINATED_JOURNALS_FRAGMENT = gql`
    ${JOURNAL_FRAGMENT}
    ${PAGE_INFO}
    fragment paginatedJournalsFields on JournalConnection {
        items {
            ...journalFields
        }
        pageInfo {
            ...pageInfoFields
        }
        totalCount
    }
`;
/*
 * Diagnoses
 */

export const DIAGNOSIS_FRAGMENT = gql`
    fragment diagnosisFields on Diagnosis {
        appointmentId
        diagnosisCodes
        DiagnosisCodes {
            code
            description
        }
        treatmentRecommendation
        subjectiveNote
        objectiveNote
        assessmentNote
        planNote
        additionalNote
        areCodesLocked
        areNotesLocked
        dateCodesLocked
        dateNotesLocked
        areNotesProcessed
        aiOriginal
    }
`;
export const DIAGNOSIS_PREVIEW_FRAGMENT = gql`
    fragment diagnosisPreviewFields on DiagnosisPreview {
        diagnosisCodes
        areCodesLocked
        areNotesLocked
        dateCodesLocked
        dateNotesLocked
        areNotesProcessed
    }
`;

/*
 * Payments
 */

export const PAYMENT_FRAGMENT = gql`
    fragment paymentFields on Payment {
        paymentId
        appointmentId
        stripePaymentIntentId
        amount
        refundAmount
        currency
        method
        status
        description
        notes
        createdAt
        updatedAt
        type
    }
`;

/*
 * Appointments
 */

export const APPOINTMENT_ACTION_OPTION_FRAGMENT = gql`
    fragment appointmentActionOptionFields on AppointmentActionOption {
        type
        default
        visibleTo
    }
`;

export const APPOINTMENT_ACTION_FRAGMENT = gql`
    ${APPOINTMENT_ACTION_OPTION_FRAGMENT}
    fragment appointmentActionFields on AppointmentAction {
        type
        options {
            ...appointmentActionOptionFields
        }
    }
`;

export const APPOINTMENT_FRAGMENT = gql`
    ${USER_MINI_FRAGMENT}
    ${APPOINTMENT_ACTION_FRAGMENT}
    ${DIAGNOSIS_PREVIEW_FRAGMENT}
    ${PAYMENT_FRAGMENT}
    ${POLICY_PREVIEW_FRAGMENT}

    # Users are embedded as userMiniFields (not the ~167-leaf userMiniFields).
    # Only the extra fields consumers of GET_APPOINTMENT / GET_NEXT_APPOINTMENT and
    # the appointment mutations actually read are added inline:
    #   client   -> clientProfile.eapOptions.isFormalReferral
    #   provider -> country.areDxCodesSupported (dx-code gating)
    #   booker   -> name only (covered by mini)
    #   guests   -> unused here (GuestList uses GET_GUESTS_FOR_APPOINTMENT); mini as a hedge
    # Extend the inline selection here if a new consumer needs more (untyped data =
    # runtime-only failure). Note: AppointmentInfoHeader passes only userId to
    # UserBanner, which refetches via GET_USER, so it imposes no requirement here.
    fragment appointmentFields on Appointment {
        appointmentId
        client {
            ...userMiniFields
            clientProfile {
                eapOptions {
                    isFormalReferral
                }
            }
        }
        provider {
            ...userMiniFields
            country {
                areDxCodesSupported
            }
        }
        booker {
            ...userMiniFields
        }
        scheduledTimezone
        duration
        startDateTime
        endDateTime
        approvalDateTime
        status
        amountPaidOut
        coverageType
        payments {
            ...paymentFields
        }
        movedByProvider
        sessionType
        sessionMode
        sessionNumber
        note
        diagnosis {
            ...diagnosisPreviewFields
        }
        policy {
            ...policyPreviewFields
        }
        guestIds
        guests {
            ...userMiniFields
        }
        createdAt
        updatedAt

        feeApplies
        isSameDay

        isClientsFirst
        isProvidersFirst

        actions {
            ...appointmentActionFields
        }
    }
`;

export const APPOINTMENT_PREVIEW_FRAGMENT = gql`
    ${USER_MINI_FRAGMENT}
    ${DIAGNOSIS_PREVIEW_FRAGMENT}
    ${POLICY_PREVIEW_FRAGMENT}

    # Users are embedded as userMiniFields (not the ~167-leaf userMiniFields).
    # Only the specific extra fields consumers actually read are added inline:
    #   client   -> country.abbreviation, clientProfile.coverageType / eapOptions.isFormalReferral
    #   provider -> country.abbreviation/liveProviderPhoneLine, providerProfile.credential.name
    #   booker   -> nothing read; kept minimal as a safety hedge
    #   guests   -> identity/display only
    # If a new consumer of an appointmentPreview-based query needs more user
    # fields, extend the inline selection here (untyped data = runtime-only failure).
    fragment appointmentPreviewFields on AppointmentPreview {
        appointmentId
        client {
            ...userMiniFields
            country {
                abbreviation
            }
            clientProfile {
                coverageType
                eapOptions {
                    isFormalReferral
                }
            }
        }
        provider {
            ...userMiniFields
            country {
                abbreviation
                liveProviderPhoneLine
            }
            providerProfile {
                credential {
                    name
                }
            }
        }
        booker {
            ...userMiniFields
        }
        scheduledTimezone
        duration
        startDateTime
        endDateTime
        approvalDateTime
        status
        amountPaidOut
        coverageType
        movedByProvider
        sessionType
        sessionMode
        sessionNumber
        diagnosis {
            ...diagnosisPreviewFields
        }
        policy {
            ...policyPreviewFields
        }
        guestIds
        guests {
            ...userMiniFields
        }
        createdAt
        updatedAt
    }
`;

export const DIAGNOSIS_WITH_APPT_FRAGMENT = gql`
    ${APPOINTMENT_PREVIEW_FRAGMENT}
    fragment diagnosisWithApptFields on DiagnosisWithAppt {
        appointmentId
        appointment {
            ...appointmentPreviewFields
        }
        diagnosisCodes
        DiagnosisCodes {
            code
            description
        }
        treatmentRecommendation
        subjectiveNote
        objectiveNote
        assessmentNote
        planNote
        additionalNote
        areCodesLocked
        areNotesLocked
        dateCodesLocked
        dateNotesLocked
        areNotesProcessed
        aiOriginal
    }
`;

export const SESSION_DATA_FRAGMENT = gql`
    ${USER_MINI_FRAGMENT}
    fragment sessionDataFields on SessionData {
        transcription
        transcriptionObject {
            type
            label
            startedAt
            startedAtLabel
            initiatedBy {
                ...userMiniFields
            }
            initiatorRole
            initiatorName
            lines {
                speaker
                speakerName
                text
            }
        }
        duration
        providerJoinTime
        providerLeaveTime
        clientJoinTime
        clientLeaveTime
        providerJoinDelta
        providerLeaveDelta
        clientJoinDelta
        clientLeaveDelta
        clientSpeakingRatio
        providerSpeakingRatio
        clientSpeechRate
        providerSpeechRate
        clientMonologueAvg
        providerMonologueAvg
        clientPatience
        providerPatience
        aiPrepGenerationStatus
        keywordList {
            client {
                key
                value
            }
            provider {
                key
                value
            }
        }
        clinicalTechniqueList {
            key
            value
        }
        eventLogs {
            type
            time
            userId
            user {
                ...userMiniFields
            }
        }
    }
`;

/** @deprecated Unused — never spread in any operation (dead code; safe to remove). */
export const SESSION_EVENT_FRAGMENT = gql`
    fragment sessionEventFields on SessionEvent {
        type
        timestamp
        metadata
    }
`;

/**
 * Groups
 */

export const GROUP_PAGE_INFO_FRAGMENT = gql`
    fragment groupPageInfoFields on GroupPageInfo {
        hasNextPage
        hasPreviousPage
        startCursor {
            id
            value
        }
        endCursor {
            id
            value
        }
    }
`;

export const NUMERIC_DATA_FIELDS_FRAGMENT = gql`
    fragment numericDataFields on NumericData {
        value
        remark
        startDateTime
        endDateTime
        unit
    }
`;

export const NUMERIC_DATA_WITH_DELTA_FIELDS_FRAGMENT = gql`
    ${NUMERIC_DATA_FIELDS_FRAGMENT}
    fragment numericDataWithDeltaFields on NumericDataWithDelta {
        value
        remark
        startDateTime
        endDateTime
        unit
        from {
            ...numericDataFields
        }
        delta {
            absolute
            percent
        }
    }
`;

export const TOPIC_FRAGMENT = gql`
    fragment topicFields on Topic {
        topicId
        name
        createdBy
        createdAt
        deletedAt
        groupCount
    }
`;

export const GROUP_RECURRENCE_RULE_FRAGMENT = gql`
    fragment groupRecurrenceRuleFields on GroupRecurrenceRule {
        groupRecurrenceRuleId
        groupId
        freq
        interval
        byweekday
        timeOfDay
        timezone
        count
        effectiveOn
        effectiveUntil
        status
        createdAt
        deletedAt
    }
`;

export const GROUP_CLIENT_PREVIEW_FRAGMENT = gql`
    ${TOPIC_FRAGMENT}
    ${GROUP_RECURRENCE_RULE_FRAGMENT}
    fragment groupClientPreviewFields on Group {
        groupId
        name
        description
        maxCapacity
        waitlistEnabled
        photoUrl
        status
        createdAt
        updatedAt
        provider {
            providerId
            firstName
            lastName
            photoUrl
        }
        groupMemberCount
        topics {
            ...topicFields
        }
        recurrenceRules {
            ...groupRecurrenceRuleFields
        }
    }
`;

export const GROUP_CLIENT_PREVIEW_CONNECTION_FRAGMENT = gql`
    ${GROUP_CLIENT_PREVIEW_FRAGMENT}
    ${GROUP_PAGE_INFO_FRAGMENT}
    fragment groupClientPreviewConnectionFields on GroupConnection {
        items {
            ...groupClientPreviewFields
        }
        pageInfo {
            ...groupPageInfoFields
        }
        totalCount
    }
`;

export const GROUP_PREVIEW_FRAGMENT = gql`
    ${TOPIC_FRAGMENT}
    ${GROUP_RECURRENCE_RULE_FRAGMENT}
    fragment groupPreviewFields on Group {
        groupId
        name
        description
        maxCapacity
        waitlistEnabled
        waitlistedMembersCount
        participantTalkTime
        cost
        photoUrl
        status
        createdAt
        updatedAt
        provider {
            providerId
            firstName
            lastName
            payRates {
                type
                rate
            }
            photoUrl
        }
        completedSessionsCount
        groupMemberCount
        topics {
            ...topicFields
        }
        recurrenceRules {
            ...groupRecurrenceRuleFields
        }
    }
`;

export const GROUP_SESSION_FRAGMENT = gql`
    ${GROUP_PREVIEW_FRAGMENT}
    fragment groupSessionFields on GroupSession {
        groupSessionId
        groupRecurrenceRuleId
        roomUrl
        groupId
        startDate
        group {
            ...groupPreviewFields
        }
        endDate
        status
        createdAt
        deletedAt
    }
`;

export const GROUP_SESSION_PREVIEW_GROUP_FRAGMENT = gql`
    fragment groupSessionPreviewGroupFields on Group {
        groupId
        name
        photoUrl
        provider {
            providerId
            firstName
            lastName
            nickName
            photoUrl
        }
    }
`;

export const GROUP_SESSION_PREVIEW_FRAGMENT = gql`
    ${GROUP_SESSION_PREVIEW_GROUP_FRAGMENT}
    fragment groupSessionPreviewFields on GroupSession {
        groupSessionId
        groupRecurrenceRuleId
        roomUrl
        groupId
        startDate
        group {
            ...groupSessionPreviewGroupFields
        }
        endDate
        status
        myRsvpStatus
        createdAt
        deletedAt
    }
`;

export const GROUP_EVENT_FRAGMENT = gql`
    ${ROLE_FRAGMENT}
    fragment groupEventFields on GroupEvent {
        groupId
        event
        reason
        createdAt
        actor {
            userId
            name
            role {
                ...roleFields
            }
        }
    }
`;

/** @deprecated Unused — never spread in any operation (dead code; safe to remove). */
export const GROUP_WITH_MEMBERS_FRAGMENT = gql`
    ${GROUP_PREVIEW_FRAGMENT}
    ${USER_FRAGMENT}
    fragment groupWithMembersFields on Group {
        ...groupPreviewFields
        groupMembers {
            ...userFields
        }
    }
`;

/** @deprecated Unused — never spread in any operation (dead code; safe to remove). */
export const GROUP_WITH_HISTORY_LOG_FRAGMENT = gql`
    ${GROUP_PREVIEW_FRAGMENT}
    ${GROUP_EVENT_FRAGMENT}
    fragment groupWithHistoryLogFields on Group {
        ...groupPreviewFields
        historyLog {
            ...groupEventFields
        }
    }
`;

/** @deprecated Unused — never spread in any operation (dead code; safe to remove). */
export const GROUP_WITH_COMPLETED_SESSIONS_FRAGMENT = gql`
    ${GROUP_PREVIEW_FRAGMENT}
    ${GROUP_SESSION_FRAGMENT}
    fragment groupWithCompletedSessionsFields on Group {
        ...groupPreviewFields
        completedSessions {
            ...groupSessionFields
        }
    }
`;

// groupMembers and completedSessions are NOT selected: no consumer reads them
// (the Members/Waitlist tabs use GET_PAGINATED_GROUP_MEMBERS; the Sessions tab
// uses GET_GROUP_SESSION_ANALYTICS). historyLog is kept — GroupHistoryLogTab and
// GroupDetailsDialog render it.
export const GROUP_FRAGMENT = gql`
    ${GROUP_PREVIEW_FRAGMENT}
    ${GROUP_EVENT_FRAGMENT}
    fragment groupFields on Group {
        ...groupPreviewFields
        historyLog {
            ...groupEventFields
        }
    }
`;

export const GROUPS_SUBSCRIPTION_PROMO_FRAGMENT = gql`
    fragment groupsSubscriptionPromoFields on GroupSubscriptionPromo {
        code
        percentOff
        amountOffCents
        currency
        duration
        durationInMonths
        redeemedAt
        quotedSubtotalCents
        quotedDiscountCents
        quotedTotalCents
    }
`;

export const GROUPS_SUBSCRIPTION_FRAGMENT = gql`
    ${GROUPS_SUBSCRIPTION_PROMO_FRAGMENT}
    fragment groupsSubscriptionFields on GroupSubscription {
        groupSubscriptionId
        userId
        groupPricingId
        interval
        externalProvider
        externalSubscriptionId
        externalCustomerId
        externalPriceId
        currentPeriodStart
        currentPeriodEnd
        metadata
        status
        endedAt
        createdAt
        updatedAt
        promo {
            ...groupsSubscriptionPromoFields
        }
    }
`;

export const REACHLINK_PLUS_SUBSCRIPTION_FRAGMENT = gql`
    fragment reachlinkPlusSubscriptionFields on Subscription {
        type
        status
        platform
        interval
        externalSubscriptionId
        autoRenew
        currentPeriodStartsAt
        currentPeriodEndsAt
        isActive
        usageSummary {
            usages {
                feature
                usage
                limit
                isUnlimited
                canUse
                shouldWarn
            }
        }
        metadata
    }
`;

export const GROUP_SESSION_PAGE_INFO_FRAGMENT = gql`
    fragment groupSessionPageInfoFields on GroupSessionPageInfo {
        hasNextPage
        hasPreviousPage
        offset
        limit
    }
`;

export const GROUP_CONNECTION_FRAGMENT = gql`
    ${GROUP_PREVIEW_FRAGMENT}
    ${GROUP_PAGE_INFO_FRAGMENT}
    fragment groupConnectionFields on GroupConnection {
        items {
            ...groupPreviewFields
        }
        pageInfo {
            ...groupPageInfoFields
        }
        totalCount
    }
`;

export const GROUP_CONNECTION_WITH_ANALYTICS_FRAGMENT = gql`
    ${GROUP_PREVIEW_FRAGMENT}
    ${NUMERIC_DATA_FIELDS_FRAGMENT}
    ${GROUP_PAGE_INFO_FRAGMENT}
    fragment groupConnectionWithAnalyticsFields on GroupConnection {
        items {
            ...groupPreviewFields
            utilizationRate {
                ...numericDataFields
            }
            engagementRate {
                ...numericDataFields
            }
            groupCost {
                ...numericDataFields
            }
            avgAttendeesPerSession {
                ...numericDataFields
            }
            completedSessionsAnalytics {
                ...numericDataFields
            }
        }
        pageInfo {
            ...groupPageInfoFields
        }
        totalCount
    }
`;

export const GROUP_CONNECTION_WITH_SESSIONS_FRAGMENT = gql`
    ${GROUP_PREVIEW_FRAGMENT}
    ${GROUP_PAGE_INFO_FRAGMENT}
    fragment groupConnectionWithSessionsFields on GroupConnection {
        items {
            ...groupPreviewFields
            sessions {
                groupSessionId
                groupId
                roomUrl
                startDate
                endDate
                status
            }
        }
        pageInfo {
            ...groupPageInfoFields
        }
        totalCount
    }
`;

export const GROUP_STATS_FRAGMENT = gql`
    fragment groupStatsFields on GroupStats {
        activeCount
        pendingCount
        waitlistEnabledCount
        draftCount
        totalCount
    }
`;

export const GROUP_SESSIONS_CONNECTION_FRAGMENT = gql`
    ${GROUP_SESSION_FRAGMENT}
    ${GROUP_SESSION_PAGE_INFO_FRAGMENT}
    fragment groupSessionsConnectionFields on GroupSessionConnection {
        items {
            ...groupSessionFields
        }
        pageInfo {
            ...groupSessionPageInfoFields
        }
        totalCount
    }
`;

export const GROUP_SESSION_PREVIEW_CONNECTION_FRAGMENT = gql`
    ${GROUP_SESSION_PREVIEW_FRAGMENT}
    ${GROUP_SESSION_PAGE_INFO_FRAGMENT}
    fragment groupSessionPreviewConnectionFields on GroupSessionConnection {
        items {
            ...groupSessionPreviewFields
        }
        pageInfo {
            ...groupSessionPageInfoFields
        }
        totalCount
    }
`;

export const GROUP_MEMBER_FRAGMENT = gql`
    fragment groupMemberFields on GroupMember {
        groupMemberId
        groupId
        userId
        memberRole
        status
        createdAt
    }
`;

export const GROUP_MEMBERSHIP_EVENT_ACTOR_FRAGMENT = gql`
    fragment groupMembershipEventActorFields on GroupMembershipEventActor {
        userId
        name
        firstName
        lastName
        photoUrl
    }
`;

export const GROUP_MEMBERSHIP_EVENT_DETAIL_FRAGMENT = gql`
    ${GROUP_MEMBERSHIP_EVENT_ACTOR_FRAGMENT}
    fragment groupMembershipEventDetailFields on GroupMembershipEventDetail {
        groupMembershipEventId
        event
        reason
        createdBy
        actor {
            ...groupMembershipEventActorFields
        }
        createdAt
    }
`;

export const GROUP_MEMBER_DETAILS_FRAGMENT = gql`
    ${GROUP_MEMBERSHIP_EVENT_DETAIL_FRAGMENT}
    fragment groupMemberDetailsFields on GroupMemberDetails {
        groupMemberId
        userId
        name
        firstName
        lastName
        photoUrl
        memberRole
        status
        dateJoined
        sessionsAttendedCount
        dateLastAttended
        attendanceRate
        groupMembershipEvents {
            ...groupMembershipEventDetailFields
        }
    }
`;

export const GROUP_MEMBER_PAGE_INFO_FRAGMENT = gql`
    fragment groupMemberPageInfoFields on GroupMemberPageInfo {
        hasNextPage
        hasPreviousPage
        offset
        limit
    }
`;

export const GROUP_MEMBER_CONNECTION_FRAGMENT = gql`
    ${GROUP_MEMBER_DETAILS_FRAGMENT}
    ${GROUP_MEMBER_PAGE_INFO_FRAGMENT}
    fragment groupMemberConnectionFields on GroupMemberConnection {
        items {
            ...groupMemberDetailsFields
        }
        pageInfo {
            ...groupMemberPageInfoFields
        }
        totalCount
    }
`;

export const GROUP_PLATFORM_CONFIG_FRAGMENT = gql`
    fragment groupPlatformConfigFields on GroupPlatformConfig {
        groupPlatformConfigId
        defaultParticipantTalkTime
        subFee
        maxGroupsInPlatform
        maxSessionsPerGroup
        maxMembersPerGroup
        effectiveFrom
        effectiveTo
        createdBy
        createdAt
    }
`;

export const ATTENDANCE_RECORD_FRAGMENT = gql`
    fragment attendanceRecordFields on AttendanceRecord {
        hasAttended
        groupSessionId
        groupRecurrenceRuleId
        roomUrl
        groupId
        startDate
        endDate
        status
        createdAt
        deletedAt
    }
`;

export const ATTENDANCE_RECORD_CONNECTION_FRAGMENT = gql`
    ${ATTENDANCE_RECORD_FRAGMENT}
    ${GROUP_SESSION_PAGE_INFO_FRAGMENT}
    fragment attendanceRecordConnectionFields on AttendanceRecordConnection {
        items {
            ...attendanceRecordFields
        }
        pageInfo {
            ...groupSessionPageInfoFields
        }
        totalCount
    }
`;

export const GROUP_MEMBERSHIP_EVENT_PAGE_INFO_FRAGMENT = gql`
    fragment groupMembershipEventPageInfoFields on GroupMembershipEventPageInfo {
        hasNextPage
        hasPreviousPage
        offset
        limit
    }
`;

export const GROUP_MEMBERSHIP_EVENT_CONNECTION_FRAGMENT = gql`
    ${GROUP_MEMBERSHIP_EVENT_DETAIL_FRAGMENT}
    ${GROUP_MEMBERSHIP_EVENT_PAGE_INFO_FRAGMENT}
    fragment groupMembershipEventConnectionFields on GroupMembershipEventConnection {
        items {
            ...groupMembershipEventDetailFields
        }
        pageInfo {
            ...groupMembershipEventPageInfoFields
        }
        totalCount
    }
`;

export const GROUP_SESSION_ANALYTICS_ITEM_FRAGMENT = gql`
    fragment groupSessionAnalyticsItemFields on GroupSessionAnalyticsItem {
        groupSessionId
        groupId
        startDate
        endDate
        status
        attendanceCount
        allMembersCount
        attendanceRate
        newMembersCount
        totalHandRaises
        totalQuestionsAsked
        createdAt
        updatedAt
    }
`;

export const GROUP_SESSION_ANALYTICS_CONNECTION_FRAGMENT = gql`
    ${GROUP_SESSION_ANALYTICS_ITEM_FRAGMENT}
    ${GROUP_SESSION_PAGE_INFO_FRAGMENT}
    fragment groupSessionAnalyticsConnectionFields on GroupSessionAnalyticsConnection {
        items {
            ...groupSessionAnalyticsItemFields
        }
        pageInfo {
            ...groupSessionPageInfoFields
        }
        totalCount
    }
`;

export const CLIENT_GROUP_MEMBERSHIP_DETAILS_FRAGMENT = gql`
    ${GROUP_CLIENT_PREVIEW_FRAGMENT}
    ${GROUP_MEMBERSHIP_EVENT_DETAIL_FRAGMENT}
    fragment clientGroupMembershipDetailsFields on ClientGroupMembershipDetails {
        groupMemberId
        group {
            ...groupClientPreviewFields
        }
        memberRole
        status
        dateJoined
        sessionsAttendedCount
        dateLastAttended
        attendanceRate
        events {
            ...groupMembershipEventDetailFields
        }
    }
`;

export const CLIENT_GROUP_MEMBERSHIP_CONNECTION_FRAGMENT = gql`
    ${CLIENT_GROUP_MEMBERSHIP_DETAILS_FRAGMENT}
    ${GROUP_MEMBER_PAGE_INFO_FRAGMENT}
    fragment clientGroupMembershipConnectionFields on ClientGroupMembershipConnection {
        items {
            ...clientGroupMembershipDetailsFields
        }
        pageInfo {
            ...groupMemberPageInfoFields
        }
        totalCount
    }
`;

export const CLIENT_OWN_GROUP_MEMBERSHIP_DETAILS_FRAGMENT = gql`
    ${GROUP_CLIENT_PREVIEW_FRAGMENT}
    fragment clientOwnGroupMembershipDetailsFields on ClientGroupMembershipDetails {
        groupMemberId
        group {
            ...groupClientPreviewFields
        }
        memberRole
        status
        dateJoined
        sessionsAttendedCount
        dateLastAttended
        attendanceRate
    }
`;

export const CLIENT_OWN_GROUP_MEMBERSHIP_CONNECTION_FRAGMENT = gql`
    ${CLIENT_OWN_GROUP_MEMBERSHIP_DETAILS_FRAGMENT}
    ${GROUP_MEMBER_PAGE_INFO_FRAGMENT}
    fragment clientOwnGroupMembershipConnectionFields on ClientGroupMembershipConnection {
        items {
            ...clientOwnGroupMembershipDetailsFields
        }
        pageInfo {
            ...groupMemberPageInfoFields
        }
        totalCount
    }
`;

/** @deprecated Unused — never spread in any operation (dead code; safe to remove). */
export const RSVP_SUMMARY_FRAGMENT = gql`
    fragment rsvpSummaryFields on RsvpSummary {
        yes
        no
        maybe
        noResponse
        total
    }
`;

export const GROUP_SESSION_RSVP_FRAGMENT = gql`
    fragment groupSessionRsvpFields on GroupSessionRsvp {
        groupSessionRsvpId
        groupSessionId
        groupMemberId
        status
        memberName
        createdAt
        updatedAt
    }
`;

export const GROUP_PROFIT_FRAGMENT = gql`
    ${NUMERIC_DATA_WITH_DELTA_FIELDS_FRAGMENT}
    fragment groupProfitFields on GroupSalesSummary {
        profit {
            ...numericDataWithDeltaFields
        }
    }
`;

export const GROUP_SALES_SUMMARY_FRAGMENT = gql`
    ${NUMERIC_DATA_WITH_DELTA_FIELDS_FRAGMENT}
    fragment groupSalesSummaryFields on GroupSalesSummary {
        profit {
            ...numericDataWithDeltaFields
        }
        revenue {
            ...numericDataWithDeltaFields
        }
        cost {
            ...numericDataWithDeltaFields
        }
    }
`;

export const GROUP_SESSIONS_SUMMARY_FRAGMENT = gql`
    ${NUMERIC_DATA_WITH_DELTA_FIELDS_FRAGMENT}
    fragment groupSessionsSummaryFields on GroupSessionsSummary {
        sessionCount {
            ...numericDataWithDeltaFields
        }
        engagementRate {
            ...numericDataWithDeltaFields
        }
        utilizationRate {
            ...numericDataWithDeltaFields
        }
    }
`;

export const GROUP_OVERALL_SUMMARY_FRAGMENT = gql`
    ${NUMERIC_DATA_WITH_DELTA_FIELDS_FRAGMENT}
    fragment groupOverallSummaryFields on OverAllSummary {
        subscribersCount {
            ...numericDataWithDeltaFields
        }
        groupsCount {
            ...numericDataWithDeltaFields
        }
        averageGroupsPerSubscriber {
            ...numericDataWithDeltaFields
        }
    }
`;

export const TIME_SERIES_POINT_FRAGMENT = gql`
    fragment TimeSeriesPointFields on TimeSeriesPoint {
        month
        startDateTime
        endDateTime
        value
        unit
    }
`;

export const GROUP_SALES_TIME_SERIES_FRAGMENT = gql`
    ${TIME_SERIES_POINT_FRAGMENT}
    fragment groupSalesTimeSeriesFields on GroupSalesTimeSeries {
        profit {
            ...TimeSeriesPointFields
        }
        revenue {
            ...TimeSeriesPointFields
        }
        cost {
            ...TimeSeriesPointFields
        }
    }
`;

export const GROUP_SESSIONS_TIME_SERIES_FRAGMENT = gql`
    ${TIME_SERIES_POINT_FRAGMENT}
    fragment groupSessionsTimeSeriesFields on GroupSessionsTimeSeries {
        engagementRate {
            ...TimeSeriesPointFields
        }
        utilizationRate {
            ...TimeSeriesPointFields
        }
    }
`;

export const SUBSCRIPTION_PLAN_PRICING_FRAGMENT = gql`
    fragment subscriptionPlanPricingFields on SubscriptionPlanPricing {
        cents
        currency
        interval
        intervalCount
        status
    }
`;

export const AVAILABLE_SUBSCRIPTION_PLAN_FRAGMENT = gql`
    ${SUBSCRIPTION_PLAN_PRICING_FRAGMENT}
    fragment availableSubscriptionPlanFields on SubscriptionPlan {
        name
        pricing {
            ...subscriptionPlanPricingFields
        }
    }
`;

export const GROUP_PROMO_CODE_VALIDATION_FRAGMENT = gql`
    fragment groupPromoCodeValidationFields on GroupPromoCodeValidation {
        valid
        code
        reason
        terms {
            percentOff
            amountOffCents
            currency
            duration
            durationInMonths
        }
        preview {
            subtotalCents
            discountCents
            totalCents
            currency
        }
    }
`;

/*
 * Payouts
 */

export const PAYOUT_FRAGMENT = gql`
    ${USER_MINI_FRAGMENT}
    fragment payoutFields on Payout {
        payoutId
        provider {
            ...userMiniFields
            role {
                name
            }
        }
        stripeConnectId
        stripeTransferId
        stripePayoutId
        amount
        currency
        type
        status
        description
        statementDescriptor
        transferGroup
        arrivalDate
        retryCount
        error
        createdAt
        updatedAt
    }
`;

// The admin payouts landing grid (ProviderPayoutManager) previously embedded a
// full userMiniFields (~167 leaves) per provider row. That grid, the drill-in
// ProviderCard, and the payout-issuing flow only read the small subset below, so
// we scope the provider embed to those fields.
// COUPLING: ProviderCard renders these provider rows with only the fields below;
// it never fires its own GET_USER because the provider is passed. If a client row
// (role CLIENT) is ever routed through this fragment/card, it would also need
// clientProfile.State/status and isRegistrationComplete.
export const OWED_PAYOUT_INFO_FRAGMENT = gql`
    fragment owedPayoutInfoFields on OwedPayoutInfo {
        provider {
            userId
            name
            firstName
            lastName
            photoUrl
            role {
                name
            }
            providerProfile {
                credential {
                    name
                }
            }
        }
        currency
        Commercial
        EAP
        Fees
        Groups
        All

        success
        error
    }
`;

// Appointment selection for payout breakdown rows. The owed-payout breakdown is
// one of the largest responses in the app because it embeds a full
// appointmentPreview (client/provider/booker/policy/diagnosis/guests) per
// appointment across four buckets. By default this fetches only the tiny subset
// the dashboard widgets need (they read `appointments.length`); the heavy
// appointmentPreview is opt-in via the `$expanded` variable so the admin drill-in
// grid (OwedPayoutInfo.tsx) can render its client/date/status/coverage/dxStatus/
// noteStatus columns. Overlapping fields (appointmentId, client) merge harmlessly
// when expanded.
// COUPLING: any operation that spreads this fragment (directly or transitively)
// MUST declare a `$expanded: Boolean` variable (default false). See the payout
// breakdown queries in resolvers/queries.ts.
// Note: the `actions` column still cannot render here even when expanded — it
// needs the `actions` field from the full appointmentFields (not
// appointmentPreview) plus an onActionClick handler.
export const PAYOUT_BREAKDOWN_APPOINTMENT_FRAGMENT = gql`
    ${APPOINTMENT_PREVIEW_FRAGMENT}
    fragment payoutBreakdownAppointmentFields on AppointmentPreview {
        appointmentId
        startDateTime
        duration
        status
        coverageType
        sessionType
        client {
            userId
            name
            firstName
            lastName
            deletedAt
        }
        ...appointmentPreviewFields @include(if: $expanded)
    }
`;

export const OWED_PAYOUT_INFO_BREAKDOWN_APPPOINTMENT_FRAGMENT = gql`
    ${PAYOUT_BREAKDOWN_APPOINTMENT_FRAGMENT}
    fragment owedPayoutInfoBreakdownAppointmentFields on OwedPayoutInfoBreakdownAppointment {
        amount
        appointment @include(if: $expanded) {
            ...payoutBreakdownAppointmentFields
        }
    }
`;

export const OWED_PAYOUT_INFO_BREAKDOWN_GROUP_SESSION_FRAGMENT = gql`
    ${GROUP_SESSION_PREVIEW_FRAGMENT}
    fragment owedPayoutInfoBreakdownGroupSessionFields on OwedPayoutInfoBreakdownGroupSession {
        amount
        session @include(if: $expanded) {
            ...groupSessionPreviewFields
        }
    }
`;

export const OWED_PAYOUT_INFO_BREAKDOWN_MEMBER_FRAGMENT = gql`
    ${OWED_PAYOUT_INFO_BREAKDOWN_APPPOINTMENT_FRAGMENT}
    ${OWED_PAYOUT_INFO_BREAKDOWN_GROUP_SESSION_FRAGMENT}
    fragment owedPayoutInfoBreakdownMemberFields on OwedPayoutInfoBreakdownMember {
        amount
        items {
            ...owedPayoutInfoBreakdownAppointmentFields
            ...owedPayoutInfoBreakdownGroupSessionFields
        }
    }
`;

export const OWED_PAYOUT_INFO_BREAKDOWN_FRAGMENT = gql`
    ${OWED_PAYOUT_INFO_BREAKDOWN_MEMBER_FRAGMENT}
    fragment owedPayoutInfoBreakdownFields on OwedPayoutInfoBreakdown {
        currency
        Commercial {
            ...owedPayoutInfoBreakdownMemberFields
        }
        EAP {
            ...owedPayoutInfoBreakdownMemberFields
        }
        Fees {
            ...owedPayoutInfoBreakdownMemberFields
        }
        Groups {
            ...owedPayoutInfoBreakdownMemberFields
        }
        All {
            ...owedPayoutInfoBreakdownMemberFields
        }
    }
`;

/*
 * Assessments
 */

export const ASSESSMENT_FRAGMENT = gql`
    ${USER_MINI_FRAGMENT}
    fragment assessmentFields on Assessment {
        assessmentId
        client {
            ...userMiniFields
        }
        type
        scores
        totalScore
        createdAt
    }
`;

/*
 * Claims
 */

export const INSURANCE_CLAIM_FRAGMENT = gql`
    ${APPOINTMENT_PREVIEW_FRAGMENT}
    ${ASSESSMENT_FRAGMENT}
    ${INSURANCE_CARRIER_FRAGMENT}
    ${STATE_FRAGMENT}
    ${POLICY_PREVIEW_FRAGMENT}

    fragment insuranceClaimFields on InsuranceClaim {
        insuranceClaimId
        encounterId
        encounterType
        encounter {
            __typename
            ... on AppointmentPreview {
                ...appointmentPreviewFields
            }
            ... on Assessment {
                ...assessmentFields
            }
        }
        policyId
        policy {
            ...policyPreviewFields
        }
        type
        clearingHouse
        trackingNumber
        cptCode
        insuranceCarrierId
        insuranceCarrier {
            ...insuranceCarrierFields
        }
        state
        State {
            ...stateFields
        }
        payerId
        status
        flag
        payerIcn
        eraStatus
        checkNumber
        note
        currency
        copay
        insAmountPaid
        hsaAmountPaid
        dateOfService
        dateBilled
        datePaid
        createdAt
        updatedAt
    }
`;

/*
 * Conversations
 */

export const MESSAGE_FRAGMENT = gql`
    fragment messageFields on Message {
        messageId
        senderId
        direction
        status
        scheduledFor
        content
        payloadData {
            subject
            plainContent
            to
            fromEmail
        }
        errorMessage
        errorCode
        createdAt
        updatedAt
    }
`;

export const CONVERSATION_FRAGMENT = gql`
    ${MESSAGE_FRAGMENT}
    # conversation.user is not read by any GET_CONVERSATION consumer (the carebot
    # chat UI sources user identity from a separately-passed prop), so only the id
    # is selected instead of the full ~349-field userFields.
    fragment conversationFields on Conversation {
        conversationId
        user {
            userId
        }
        name
        isActive
        messages {
            ...messageFields
        }
        createdAt
        updatedAt
    }
`;

export const CONVERSATION_PREVIEW_FRAGMENT = gql`
    fragment conversationPreviewFields on ConversationPreview {
        conversationId
        name
        isActive
        createdAt
        updatedAt
    }
`;

export const MEDICAL_DOCUMENT_FRAGMENT = gql`
    fragment medicalDocumentFields on MedicalDocument {
        id
        type
        title
        color
        createdAt
        appointmentDate
        clientUserId
    }
`;

export const FAX_JOB_FRAGMENT = gql`
    fragment faxJobFields on FaxJob {
        faxJobId
        status
        recipientFax
        clientUserId
        clientUser {
            userId
            firstName
            lastName
            name
        }
        requestedById
        requestedBy {
            userId
            firstName
            lastName
            name
        }
        errorMessage
        completedAt
        createdAt
        updatedAt
        documentSnapshot {
            id
            title
            type
            createdAt
        }
        s3Key
        s3Bucket
        jobHistory {
            id
            type
            status
            startedAt
            completedAt
            errorMessage
            metadata {
                pageCount
                fileSizeBytes
                confirmationId
                transmissionDuration
                errorType
                errorCode
                failureStep
            }
        }
    }
`;
/*
 * Assignments
 */

export const ASSIGNMENT_COMPONENT_CHILD_FRAGMENT = gql`
    fragment assignmentComponentChildFields on AssignmentComponent {
        assignmentComponentId
        assignmentPageId
        parentComponentId
        type
        name
        label
        title
        content
        source
        width
        height
        placeholder
        rows
        min
        max
        step
        variant
        color
        fontSize
        alignment
        defaultValueText
        defaultValueNumber
        defaultValueBoolean
        labelsMin
        labelsMax
        labelsMidpoints
        isInput
        sortOrder
        metadata
        options {
            value
            label
            sortOrder
        }
        validation {
            required
            minLength
            maxLength
            pattern
            patternMessage
            minSelections
            maxSelections
            minDate
            maxDate
        }
    }
`;

export const ASSIGNMENT_COMPONENT_FRAGMENT = gql`
    ${ASSIGNMENT_COMPONENT_CHILD_FRAGMENT}
    fragment assignmentComponentFields on AssignmentComponent {
        assignmentComponentId
        assignmentPageId
        parentComponentId
        type
        name
        label
        title
        content
        source
        width
        height
        placeholder
        rows
        min
        max
        step
        variant
        color
        fontSize
        alignment
        defaultValueText
        defaultValueNumber
        defaultValueBoolean
        labelsMin
        labelsMax
        labelsMidpoints
        isInput
        sortOrder
        metadata
        options {
            value
            label
            sortOrder
        }
        validation {
            required
            minLength
            maxLength
            pattern
            patternMessage
            minSelections
            maxSelections
            minDate
            maxDate
        }
        children {
            ...assignmentComponentChildFields
        }
    }
`;

export const ASSIGNMENT_PAGE_FRAGMENT = gql`
    ${ASSIGNMENT_COMPONENT_FRAGMENT}
    fragment assignmentPageFields on AssignmentPage {
        assignmentPageId
        assignmentId
        title
        sortOrder
        components {
            ...assignmentComponentFields
        }
    }
`;

export const ASSIGNMENT_FRAGMENT = gql`
    ${ASSIGNMENT_PAGE_FRAGMENT}
    fragment assignmentFields on Assignment {
        assignmentId
        slug
        name
        description
        active
        isActive
        pages {
            ...assignmentPageFields
        }
        createdAt
        updatedAt
    }
`;

// The assignment schema (pages/components) is NOT fetched here — it is sourced
// client-side (assignmentRegistry.ts + per-slug modules), so only assignment
// identity fields are selected. The heavy `inference` subtree is opt-in via
// `$withInference`: submission LIST views omit it (not rendered per row), while
// the single detail view passes `withInference: true` as a first-paint fallback
// before the authoritative network-only GET_ASSIGNMENT_INFERENCE resolves.
// COUPLING: any operation spreading this fragment (directly or via
// paginatedAssignmentSubmissionsFields) MUST declare `$withInference: Boolean`.
export const ASSIGNMENT_SUBMISSION_FRAGMENT = gql`
    fragment assignmentSubmissionFields on AssignmentSubmission {
        assignmentSubmissionId
        submittedVia
        answers {
            answerKey
            valueType
            value
            textValue
            numberValue
            booleanValue
        }
        assignment {
            slug
            name
            description
        }
        inference @include(if: $withInference) {
            assignmentInferenceId
            status
            title
            summary
            keyInsights {
                body
                sortOrder
            }
            emotions {
                emotionText
                emoji
                sortOrder
            }
            suggestedGoals {
                assignmentInferenceGoalSuggestionId
                goalType
                body
                acceptedGoalId
            }
            patterns {
                body
                sortOrder
            }
        }
        createdAt
    }
`;

export const PAGINATED_ASSIGNMENT_SUBMISSIONS_FRAGMENT = gql`
    ${ASSIGNMENT_SUBMISSION_FRAGMENT}
    fragment paginatedAssignmentSubmissionsFields on AssignmentSubmissionConnection {
        items {
            ...assignmentSubmissionFields
        }
        pageInfo {
            hasNextPage
            hasPreviousPage
            startCursor {
                id
                timestamp
            }
            endCursor {
                id
                timestamp
            }
        }
        totalCount
    }
`;

export const CLIENT_ASSIGNMENT_FRAGMENT = gql`
    fragment clientAssignmentFields on ClientAssignment {
        clientAssignmentId
        assignmentSlug
        assignmentName
        clientId
        providerId
        assignmentSubmissionId
        status
        dueDate
        assignedAt
        completedAt
        unassignedAt
        createdAt
        updatedAt
        submission {
            assignmentSubmissionId
        }
    }
`;

/*
 * Analytics
 */

export const METRIC_FRAGMENT = gql`
    fragment metricFields on Metric {
        value
        period {
            start
            end
        }
    }
`;

export const DELTA_FRAGMENT = gql`
    fragment deltaFields on Delta {
        absolute
        percentage
    }
`;

/*
 * Messaging
 */

export const CLIENT_PROVIDER_RELATIONSHIP_FRAGMENT = gql`
    fragment clientProviderRelationshipFields on ClientProviderRelationship {
        firstOccurredAppointment {
            appointmentId
            startDateTime
        }
        latestOccurredAppointment {
            appointmentId
            startDateTime
        }
    }
`;

export const SAFETY_RESOURCES_FRAGMENT = gql`
    fragment safetyResourcesFields on SafetyResources {
        countryId
        hotlineNumber
        hotlineLabel
    }
`;

export const REPORTED_MESSAGE_FRAGMENT = gql`
    fragment reportedMessageFields on ReportedMessage {
        reportedMessageId
        messageId
        conversationId
        kind
        severity
        title
        summary
        createdAt
    }
`;

export const MESSAGING_MESSAGE_FRAGMENT = gql`
    fragment messagingMessageFields on MessagingMessage {
        messageId
        conversationId
        sender {
            userId
            name
            firstName
            lastName
            photoUrl
            role {
                name
            }
        }
        kind
        direction
        status
        content
        contentFormat
        createdAt
        updatedAt
    }
`;

export const ESCALATION_RESOLUTION_FRAGMENT = gql`
    fragment escalationResolutionFields on EscalationResolution {
        escalationResolutionId
        resolvedBy {
            userId
            name
            firstName
            lastName
            photoUrl
        }
        externalProvider
        externalTicketUrl
        resolvedAt
    }
`;

export const MESSAGING_RECIPIENT_FRAGMENT = gql`
    ${CLIENT_PROVIDER_RELATIONSHIP_FRAGMENT}
    fragment messagingRecipientFields on MessagingRecipient {
        user {
            userId
            name
            firstName
            lastName
            photoUrl
            providerProfile {
                credential {
                    credentialId
                    name
                }
            }
        }
        counterpartyRole
        messagingDisabled
        hasExistingConversation
        isInCaseload
        relationship {
            ...clientProviderRelationshipFields
        }
    }
`;

export const MESSAGE_CONNECTION_FRAGMENT = gql`
    ${MESSAGING_MESSAGE_FRAGMENT}
    ${PAGE_INFO}
    fragment messageConnectionFields on MessagingMessageConnection {
        items {
            ...messagingMessageFields
        }
        pageInfo {
            ...pageInfoFields
        }
        totalCount
    }
`;

export const MESSAGING_RECIPIENT_CONNECTION_FRAGMENT = gql`
    ${MESSAGING_RECIPIENT_FRAGMENT}
    ${PAGE_INFO}
    fragment messagingRecipientConnectionFields on MessagingRecipientConnection {
        items {
            ...messagingRecipientFields
        }
        pageInfo {
            ...pageInfoFields
        }
        totalCount
    }
`;

export const ESCALATION_PREVIEW_FRAGMENT = gql`
    ${ESCALATION_RESOLUTION_FRAGMENT}
    fragment escalationPreviewFields on Escalation {
        escalationId
        escalationRefNumber
        conversationId
        escalatedBy {
            userId
            name
            firstName
            lastName
            photoUrl
        }
        assignedAdmin {
            userId
            name
            firstName
            lastName
            photoUrl
        }
        remarks
        status
        resolution {
            ...escalationResolutionFields
        }
        createdAt
        updatedAt
    }
`;

export const MESSAGING_CONVERSATION_PREVIEW_FRAGMENT = gql`
    ${MESSAGING_MESSAGE_FRAGMENT}
    ${ROLE_FRAGMENT}
    fragment messagingConversationPreviewFields on MessagingConversation {
        conversationId
        kind
        counterparty {
            userId
            name
            photoUrl
            providerProfile {
                credential {
                    credentialId
                    name
                }
            }
        }
        participants {
            userId
            name
            photoUrl
            role {
                ...roleFields
            }
        }
        lastMessage {
            ...messagingMessageFields
        }
        unreadCount
        viewerLastReadMessageId
        counterpartyMessagingDisabled
        remainingClientMessageQuota
        safetyFlaggedAt
        createdAt
        updatedAt
    }
`;

// anchorMessage and contextMessages (a nested MessagingMessageConnection) are
// intentionally NOT selected: they have no UI reader. Escalation/conversation
// detail views fetch messages via the separate GET_CONVERSATION_MESSAGE_LIST.
export const ESCALATION_WITHOUT_CONVERSATION_FRAGMENT = gql`
    ${ESCALATION_RESOLUTION_FRAGMENT}
    fragment escalationWithoutConversationFields on Escalation {
        escalationId
        escalationRefNumber
        conversationId
        escalatedBy {
            userId
            name
            firstName
            lastName
            photoUrl
        }
        assignedAdmin {
            userId
            name
            firstName
            lastName
            photoUrl
        }
        remarks
        status
        resolution {
            ...escalationResolutionFields
        }
        createdAt
        updatedAt
    }
`;

/** @deprecated Unused — never spread in any operation (dead code; safe to remove). */
export const MESSAGING_CONVERSATION_WITHOUT_ESCALATION_FRAGMENT = gql`
    ${MESSAGING_MESSAGE_FRAGMENT}
    ${ROLE_FRAGMENT}
    ${CLIENT_PROVIDER_RELATIONSHIP_FRAGMENT}
    ${REPORTED_MESSAGE_FRAGMENT}
    fragment messagingConversationWithoutEscalationFields on MessagingConversation {
        conversationId
        kind
        counterparty {
            userId
            name
            photoUrl
            providerProfile {
                credential {
                    credentialId
                    name
                }
            }
        }
        participants {
            userId
            name
            photoUrl
            role {
                ...roleFields
            }
        }
        lastMessage {
            ...messagingMessageFields
        }
        unreadCount
        viewerLastReadMessageId
        counterpartyMessagingDisabled
        remainingClientMessageQuota
        activeReportedMessages {
            ...reportedMessageFields
        }
        # safetyResources {
        #     ...safetyResourcesFields
        # }
        relationship {
            ...clientProviderRelationshipFields
        }
        safetyFlaggedAt
        createdAt
        updatedAt
    }
`;

// The escalation list rows only read conversation.participants (for client-name
// derivation), so `conversation` is scoped to that instead of the full
// messagingConversationWithoutEscalationFields subtree. anchorMessage and
// contextMessages have no UI reader (detail views use GET_CONVERSATION_MESSAGE_LIST).
export const ESCALATION_FRAGMENT = gql`
    ${ESCALATION_RESOLUTION_FRAGMENT}
    fragment escalationFields on Escalation {
        escalationId
        escalationRefNumber
        conversationId
        conversation {
            conversationId
            participants {
                userId
                name
                photoUrl
                role {
                    name
                }
            }
        }
        escalatedBy {
            userId
            name
            firstName
            lastName
            photoUrl
        }
        assignedAdmin {
            userId
            name
            firstName
            lastName
            photoUrl
        }
        remarks
        status
        resolution {
            ...escalationResolutionFields
        }
        createdAt
        updatedAt
    }
`;

export const MESSAGING_CONVERSATION_FRAGMENT = gql`
    ${MESSAGING_MESSAGE_FRAGMENT}
    ${ROLE_FRAGMENT}
    ${CLIENT_PROVIDER_RELATIONSHIP_FRAGMENT}
    ${ESCALATION_WITHOUT_CONVERSATION_FRAGMENT}
    ${REPORTED_MESSAGE_FRAGMENT}
    fragment messagingConversationFields on MessagingConversation {
        conversationId
        kind
        counterparty {
            userId
            name
            photoUrl
            providerProfile {
                credential {
                    credentialId
                    name
                }
            }
        }
        participants {
            userId
            name
            photoUrl
            role {
                ...roleFields
            }
        }
        lastMessage {
            ...messagingMessageFields
        }
        unreadCount
        viewerLastReadMessageId
        counterpartyMessagingDisabled
        remainingClientMessageQuota
        pendingEscalation {
            ...escalationWithoutConversationFields
        }
        # activeReportedMessages and relationship are only read by the conversation
        # DETAIL view (ChatBox/ClientDetails), not by list rows, so they are opt-in
        # via $withConversationDetail. Any operation spreading this fragment must
        # declare $withConversationDetail: Boolean (default false); the detail query
        # passes true.
        activeReportedMessages @include(if: $withConversationDetail) {
            ...reportedMessageFields
        }
        # safetyResources {
        #     ...safetyResourcesFields
        # }
        relationship @include(if: $withConversationDetail) {
            ...clientProviderRelationshipFields
        }
        safetyFlaggedAt
        createdAt
        updatedAt
    }
`;

export const MESSAGING_CONVERSATION_PREVIEW_CONNECTION_FRAGMENT = gql`
    ${MESSAGING_CONVERSATION_PREVIEW_FRAGMENT}
    ${PAGE_INFO}
    fragment messagingConversationPreviewConnectionFields on MessagingConversationConnection {
        items {
            ...messagingConversationPreviewFields
        }
        pageInfo {
            ...pageInfoFields
        }
        totalCount
    }
`;

export const MESSAGING_CONVERSATION_CONNECTION_FRAGMENT = gql`
    ${MESSAGING_CONVERSATION_FRAGMENT}
    ${PAGE_INFO}
    fragment messagingConversationConnectionFields on MessagingConversationConnection {
        items {
            ...messagingConversationFields
        }
        pageInfo {
            ...pageInfoFields
        }
        totalCount
    }
`;

export const ESCALATION_PREVIEW_CONNECTION_FRAGMENT = gql`
    ${ESCALATION_PREVIEW_FRAGMENT}
    ${PAGE_INFO}
    fragment escalationPreviewConnectionFields on EscalationConnection {
        items {
            ...escalationPreviewFields
        }
        pageInfo {
            ...pageInfoFields
        }
        totalCount
    }
`;

export const ESCALATION_CONNECTION_FRAGMENT = gql`
    ${ESCALATION_FRAGMENT}
    ${PAGE_INFO}
    fragment escalationConnectionFields on EscalationConnection {
        items {
            ...escalationFields
        }
        pageInfo {
            ...pageInfoFields
        }
        totalCount
    }
`;

export const REPORTED_MESSAGE_CONNECTION_FRAGMENT = gql`
    ${REPORTED_MESSAGE_FRAGMENT}
    ${PAGE_INFO}
    fragment reportedMessageConnectionFields on ReportedMessageConnection {
        items {
            ...reportedMessageFields
        }
        pageInfo {
            ...pageInfoFields
        }
        totalCount
    }
`;

/*
 * Care Circle Referral
 */

export const REFERRAL_CODE_FRAGMENT = gql`
    fragment referralCodeFields on ReferralCode {
        ccReferralCode
        shareUrl
        shareMessage
    }
`;

export const CC_REFERRAL_PROGRESS_FRAGMENT = gql`
    fragment ccReferralProgressFields on CCReferralProgress {
        ccReferralProgressId
        level {
            level
            name
            description
            referralsToComplete
            rewardMonths
        }
        status
        completedCount
        referrals {
            name
            completedAt
        }
        remainingToNextReward
        redeemable
        completedAt
        redeemedAt
    }
`;

export const CALL_CONNECT_FRAGMENT = gql`
    fragment callConnectFields on CallConnect {
        callConnectId
        callConnectStatus
        providerCallStatus
        clientCallStatus
        initiator
        initiatorName
        attempts
        maxAttempts
        callStartTime
        callEndTime
        callDuration
        transcriptionReady
        createdAt
        isClientInVideoSession
        isProviderInVideoSession
    }
`;

/*
 * Provider Feedback
 */

export const PROVIDER_FEEDBACK_USER_FRAGMENT = gql`
    ${ROLE_FRAGMENT}
    fragment providerFeedbackUserFields on UserPreview {
        userId
        name
        photoUrl
        role {
            ...roleFields
        }
    }
`;

export const PROVIDER_FEEDBACK_ACTION_ITEM_FRAGMENT = gql`
    fragment providerFeedbackActionItemFields on ProviderFeedbackActionItem {
        providerFeedbackActionItemId
        providerFeedbackLogId
        text
        completedAt
        dismissedAt
        createdAt
    }
`;

export const PROVIDER_FEEDBACK_LOG_LIST_ITEM_FRAGMENT = gql`
    ${PROVIDER_FEEDBACK_USER_FRAGMENT}
    ${PROVIDER_FEEDBACK_ACTION_ITEM_FRAGMENT}
    fragment providerFeedbackLogListItemFields on ProviderFeedbackLog {
        providerFeedbackLogId
        providerUserId
        reporterUserId
        category
        issue
        specifyIssue
        feedback
        channel
        reporterType
        status
        critical
        createdAt
        provider {
            ...providerFeedbackUserFields
        }
        reporter {
            ...providerFeedbackUserFields
        }
        investigatedByUserId
        investigatedAt
        investigatedBy {
            ...providerFeedbackUserFields
        }
        actionItems {
            ...providerFeedbackActionItemFields
        }
        disposition
        resolutionNotes
        resolvedByUserId
        resolvedAt
        resolvedBy {
            ...providerFeedbackUserFields
        }
    }
`;

export const PROVIDER_FEEDBACK_LOG_DETAIL_FRAGMENT = gql`
    ${PROVIDER_FEEDBACK_LOG_LIST_ITEM_FRAGMENT}
    fragment providerFeedbackLogDetailFields on ProviderFeedbackLog {
        ...providerFeedbackLogListItemFields
        clientUserId
        appointmentId
        groupSessionId
        groupId
        client {
            ...providerFeedbackUserFields
        }
        investigationNotes
        attachments {
            providerFeedbackAttachmentId
            uploadId
            fileName
            mimeType
            url
        }
    }
`;
